/**
 * Formatting helpers for the three timeline date display modes.
 */

/**
 * Format a timestamp as an absolute date/time string.
 * - Same day: "14:32"
 * - Different day: "Apr 15, 14:32"
 */
export function formatAbsolute(date: Date): string {
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const time = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  if (isToday) {
    return time;
  }

  const monthDay = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return `${monthDay}, ${time}`;
}

/**
 * Format the time elapsed between two dates as a compact delta string.
 * Examples: "+12s", "+5m", "+2h", "+3d"
 *
 * `reference` is the later date (closer to now), `target` is the earlier date.
 * When computing for the first (most recent) event, `reference` should be `new Date()`.
 */
export function formatDelta(reference: Date, target: Date): string {
  const diffMs = Math.abs(reference.getTime() - target.getTime());
  const diffSeconds = Math.round(diffMs / 1000);

  if (diffSeconds < 60) {
    return `+${diffSeconds}s`;
  }

  const diffMinutes = Math.round(diffMs / (1000 * 60));
  if (diffMinutes < 60) {
    return `+${diffMinutes}m`;
  }

  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) {
    return `+${diffHours}h`;
  }

  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  return `+${diffDays}d`;
}

/**
 * Format a relative time string for tooltip usage.
 * Uses Intl.RelativeTimeFormat for consistency with the react-intl output.
 */
export function formatRelative(date: Date): string {
  const now = new Date();
  const diffInSeconds = (date.getTime() - now.getTime()) / 1000;
  const absDiff = Math.abs(diffInSeconds);

  let value: number;
  let unit: Intl.RelativeTimeFormatUnit;

  if (absDiff < 60) {
    value = Math.round(diffInSeconds);
    unit = 'second';
  } else if (absDiff < 3600) {
    value = Math.round(diffInSeconds / 60);
    unit = 'minute';
  } else if (absDiff < 86400) {
    value = Math.round(diffInSeconds / 3600);
    unit = 'hour';
  } else if (absDiff < 604800) {
    value = Math.round(diffInSeconds / 86400);
    unit = 'day';
  } else if (absDiff < 2592000) {
    value = Math.round(diffInSeconds / 604800);
    unit = 'week';
  } else if (absDiff < 31536000) {
    value = Math.round(diffInSeconds / 2592000);
    unit = 'month';
  } else {
    value = Math.round(diffInSeconds / 31536000);
    unit = 'year';
  }

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  return rtf.format(value, unit);
}
